import type React from 'react';
import type { ReactNode } from 'react';
import { useCallback, useMemo, useRef } from 'react';
import {
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandLoading,
} from '../../../../components/ui/command.tsx';
import { ResponsiveCommand } from '../../../../components/ui/responsive-command.tsx';
import type { SelectTriggerButtonProps } from '../../../../components/ui/select-trigger-button.tsx';
import { SelectTriggerButton } from '../../../../components/ui/select-trigger-button.tsx';
import Spinner from '../../../../components/ui/spinner.tsx';
import { Text } from '../../../../components/ui/text.tsx';
import type { UseControlledStateProps } from '../../../../lib/hooks/use-controlled-state.ts';
import { useControlledState } from '../../../../lib/hooks/use-controlled-state.ts';
import { useLocale } from '../../../../lib/hooks/use-locale.ts';
import { cn } from '../../../../lib/utils.ts';

export type GroupedItemType<T> = {
    heading?: ReactNode;
    items: T[];
    id: string;
};

export type SearchComboboxProps<T> = {
    isLoading: boolean;
    groupedItems: GroupedItemType<T>[];
    notFoundPlaceholder: string;
    inputPlaceholder: string;
    variant?: SelectTriggerButtonProps['variant'];
    keySelector: (item: T) => string;
    valueSelector: (item: T) => string;
    /**
     * Custom keywords selector for each command menu item.
     * It should return an array of keywords to be used for filtering.
     */
    keywordsSelector?: (item: T) => string[];
    renderItem: (item: T) => React.ReactNode;
    renderSelectedItem?: (item: T) => React.ReactNode;
    onValueChange: (id: string, item?: T) => void;
    selectedValue: string;
    className?: string;
    open?: UseControlledStateProps<boolean>['state'];
    setOpen?: UseControlledStateProps<boolean>['setState'];
    searchQuery?: UseControlledStateProps<string>['state'];
    onSearchQueryChange?: UseControlledStateProps<string>['setState'];
    /**
     * Custom filter function for whether each command menu item should matches the given search query.
     * It should return a number between 0 and 1, with 1 being the best match and 0 being hidden entirely.
     * By default, uses the `command-score` library.
     */
    filter?: (value: string, search: string, keywords?: string[]) => number;
    alwaysScrollToTop?: boolean;
    closeOnSelect?: boolean;
    disabled?: boolean;
    contentClassName?: string;
    enableCreation?: boolean;
    createLabel?: (value: string) => React.ReactNode;
    onCreate?: (value: string) => void;
    isCreateValueValid?: (value: string) => boolean;
} & React.PropsWithChildren;

export function SearchCombobox<T extends object>({
    variant,
    isLoading,
    groupedItems,
    notFoundPlaceholder,
    inputPlaceholder,
    keySelector,
    valueSelector,
    renderItem,
    onValueChange,
    children,
    selectedValue,
    className,
    filter,
    renderSelectedItem = renderItem,
    keywordsSelector,
    alwaysScrollToTop = false,
    closeOnSelect = true,
    contentClassName,
    enableCreation = false,
    createLabel,
    onCreate,
    isCreateValueValid,
    ...props
}: SearchComboboxProps<T>): React.JSX.Element {
    const { t } = useLocale();

    const selectedItem = useMemo<T | undefined>(() => {
        return groupedItems
            .flatMap((group) => group.items)
            .find((item) => keySelector(item) === selectedValue);
    }, [groupedItems, keySelector, selectedValue]);

    const [open, setOpen] = useControlledState({
        state: props.open,
        setState: props.setOpen,
        defaultState: false,
    });

    const [searchQuery, setSearchQuery] = useControlledState<string>({
        state: props.searchQuery,
        setState: props.onSearchQueryChange,
        defaultState: '',
    });

    const ref = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    const scrollUpWhenChange = useCallback(
        (value: string) => {
            setSearchQuery(value);

            if (alwaysScrollToTop) {
                requestAnimationFrame(() => {
                    listRef.current?.scrollTo({ top: 0 });
                });
            }
        },
        [alwaysScrollToTop, setSearchQuery],
    );

    const handleSelect = (id: string): void => {
        const item = groupedItems.flatMap((group) => group.items).find((item) => keySelector(item) === id);

        onValueChange(id, item);

        if (closeOnSelect) {
            setOpen(false);
        }
    };

    const shouldFilter = !!filter || !!keywordsSelector;

    const trimmedSearchQuery = searchQuery.trim();
    const canCreate =
        enableCreation &&
        !!onCreate &&
        !!trimmedSearchQuery &&
        (isCreateValueValid ? isCreateValueValid(trimmedSearchQuery) : true);

    const handleCreateOnEnter = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key !== 'Enter' || !canCreate) {
                return;
            }

            const hasVisibleItems = listRef.current?.querySelector('[cmdk-item]:not([aria-hidden="true"])');
            if (hasVisibleItems) {
                return;
            }

            e.preventDefault();
            onCreate(trimmedSearchQuery);
            setSearchQuery('');
            setOpen(false);
        },
        [canCreate, onCreate, trimmedSearchQuery, setSearchQuery, setOpen],
    );

    const triggerContent = children ?? (
        <SelectTriggerButton
            variant={variant}
            className={variant === 'inline' ? '' : 'h-auto min-h-8'}
            disabled={props.disabled}
            isLoading={isLoading}
        >
            {selectedItem ? (
                renderSelectedItem(selectedItem)
            ) : (
                <Text color="neutral-600">{inputPlaceholder}</Text>
            )}
        </SelectTriggerButton>
    );

    return (
        <div ref={ref} className={className}>
            <ResponsiveCommand
                open={open}
                onOpenChange={setOpen}
                value={selectedValue}
                shouldFilter={shouldFilter}
                filter={filter}
            >
                <ResponsiveCommand.Trigger>{triggerContent}</ResponsiveCommand.Trigger>
                <ResponsiveCommand.Content
                    className={cn(
                        'min-w-(--radix-popover-trigger-width)',
                        'w-(--radix-popover-trigger-width)',
                        contentClassName,
                    )}
                    side="bottom"
                    align="start"
                >
                    <CommandInput
                        placeholder={inputPlaceholder}
                        value={searchQuery}
                        onValueChange={scrollUpWhenChange}
                        onKeyDown={handleCreateOnEnter}
                    />
                    <CommandList ref={listRef}>
                        {!isLoading && (
                            <CommandEmpty>
                                {canCreate ? (
                                    <div
                                        role="option"
                                        aria-selected="false"
                                        tabIndex={0}
                                        className="hover:bg-accent relative flex cursor-default select-none items-center rounded-lg px-2 text-sm"
                                        onClick={() => {
                                            onCreate(trimmedSearchQuery);
                                            setSearchQuery('');
                                            setOpen(false);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                onCreate(trimmedSearchQuery);
                                                setSearchQuery('');
                                                setOpen(false);
                                            }
                                        }}
                                    >
                                        {createLabel ? createLabel(trimmedSearchQuery) : trimmedSearchQuery}
                                    </div>
                                ) : (
                                    notFoundPlaceholder || t('nothing_found')
                                )}
                            </CommandEmpty>
                        )}
                        {groupedItems.flatMap((item) => item.items).length === 0 && isLoading && (
                            <CommandLoading>
                                <Spinner size={10} />
                            </CommandLoading>
                        )}
                        {groupedItems.map(({ heading, items, id }) => (
                            <CommandGroup
                                heading={heading}
                                key={id}
                                className={items.length === 0 ? 'hidden' : ''}
                            >
                                {items.map((item) => (
                                    <CommandItem
                                        key={keySelector(item)}
                                        value={valueSelector(item)}
                                        onSelect={handleSelect}
                                        keywords={keywordsSelector?.(item) ?? []}
                                    >
                                        {renderItem(item)}
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        ))}
                    </CommandList>
                </ResponsiveCommand.Content>
            </ResponsiveCommand>
        </div>
    );
}

SearchCombobox.TriggerButton = SelectTriggerButton;
